import React, { useEffect, useRef } from 'react';
import { Phone, ExternalLink } from 'lucide-react';

const helplines = [
  { name: 'Samaritans Ireland', description: 'Confidential emotional support, any time of day or night', phone: '116 123', href: 'https://www.samaritans.org/ireland/', available: '24/7 — free call' },
  { name: 'Pieta House', description: 'Support for those in suicidal distress or engaging in self-harm', phone: '1800 247 247', href: 'https://www.pieta.ie/', available: '24/7 — free call' },
  { name: 'Crisis Text Line', description: 'Free, confidential text-based crisis support', phone: 'Text HELLO to 50808', href: 'https://www.crisistextline.ie/', available: '24/7 — free text' },
  { name: 'AWARE', description: 'Support for those affected by depression and bipolar disorder', phone: '1800 804 848', href: 'https://www.aware.ie/', available: '10am–10pm daily' },
  { name: 'Bodywhys', description: 'Eating disorder support helpline and online support groups', phone: '01 210 7906', href: 'https://www.bodywhys.ie/', available: 'Mon–Fri, office hours' },
  { name: 'ISPCC Childline', description: 'Confidential support for children and young people', phone: '1800 66 66 66', href: 'https://www.ispcc.ie/childline/', available: '24/7 — free call' },
  { name: 'Dublin Rape Crisis Centre', description: 'Confidential support for survivors of sexual violence and rape', phone: '1800 778 888', href: 'https://www.drcc.ie/', available: '24/7 — free call' },
  { name: 'One in Four', description: 'Support and therapy for adults who experienced childhood sexual abuse', phone: '01 662 4070', href: 'https://oneinfour.ie/', available: 'Mon–Fri, office hours' },
  {
    name: "Men's Aid & Women's Aid",
    description: "Support for those experiencing domestic abuse",
    combined: true,
    phones: [
      { label: "Men's Aid", phone: '01 554 3811', href: 'https://www.mensaid.ie/' },
      { label: "Women's Aid", phone: '1800 341 900', href: 'https://www.womensaid.ie/get-help/support-services/' },
    ],
  },
  { name: 'HSE Drugs & Alcohol Helpline', description: 'Free information, support and referral for substance use concerns', phone: '1800 459 459', href: 'https://www.drugs.ie/phone', available: 'Mon–Fri, 9:30am–5:30pm' },
];

function HelpCard({ h, i }) {
  if (h.combined) {
    return (
      <div
        className="bg-white border border-[#D1D8D0] rounded-xl p-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 reveal"
        style={{ transitionDelay: `${i * 0.04}s` }}
        data-testid="helpline-domestic-violence"
      >
        <h3 className="font-body font-semibold text-xs text-[#2C362A] mb-1 leading-snug">{h.name}</h3>
        <p className="font-body text-xs text-[#5B6E56] leading-snug mb-2">{h.description}</p>
        {h.phones.map((p) => (
          <div key={p.label} className="mb-1">
            <span className="font-body text-xs text-[#A8B5A3]">{p.label}: </span>
            <a href={`tel:${p.phone.replace(/\s/g, '')}`} className="font-body font-semibold text-xs text-[#708A72] hover:text-[#5D755F] transition-colors">
              {p.phone}
            </a>
            {' '}<a href={p.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 font-body text-xs text-[#5B6E56] hover:text-[#708A72] transition-colors">
              <ExternalLink size={9} strokeWidth={1.5} />
            </a>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div
      className="bg-white border border-[#D1D8D0] rounded-xl p-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 reveal"
      style={{ transitionDelay: `${i * 0.04}s` }}
      data-testid={`helpline-${h.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
    >
      <h3 className="font-body font-semibold text-xs text-[#2C362A] mb-1 leading-snug">{h.name}</h3>
      <p className="font-body text-xs text-[#5B6E56] leading-snug mb-2">{h.description}</p>
      <a href={`tel:${h.phone.replace(/\s/g, '')}`} className="flex items-center gap-1 font-body font-semibold text-xs text-[#708A72] hover:text-[#5D755F] transition-colors mb-0.5">
        <Phone size={11} strokeWidth={1.5} />{h.phone}
      </a>
      <p className="font-body text-xs text-[#708A72] mb-2">{h.available}</p>
      <a href={h.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-body text-xs text-[#5B6E56] hover:text-[#708A72] transition-colors">
        Website <ExternalLink size={9} strokeWidth={1.5} />
      </a>
    </div>
  );
}

export default function AdditionalSupport() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) el.querySelectorAll('.reveal').forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="support" className="py-24 lg:py-32 bg-[#F2F4ED]" data-testid="additional-support-section" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12 reveal">
          <h2 className="font-heading text-4xl md:text-5xl text-[#2C362A] leading-tight mb-4">Additional Support</h2>
          <p className="font-body text-base md:text-lg text-[#5B6E56] leading-relaxed max-w-2xl">
            If you need immediate support or are in crisis, please reach out to one of the following services. You are not alone.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {helplines.map((h, i) => (
            <HelpCard key={h.name} h={h} i={i} />
          ))}
        </div>

        <div className="mt-10 reveal">
          <div className="bg-white border border-[#D1D8D0] rounded-2xl p-5 flex items-start gap-4 max-w-2xl">
            <div className="w-1.5 min-h-[2rem] rounded-full bg-[#708A72] flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="font-body font-semibold text-sm text-[#2C362A] mb-1">In an emergency</p>
              <p className="font-body text-sm text-[#5B6E56] leading-relaxed">
                If you or someone else is in immediate danger, please call <strong>999 or 112</strong> or go to your nearest Emergency Department.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
